/* eslint-disable react/display-name */
import React, { ReactNode, forwardRef, useState } from 'react';
import { InputNumberProps } from 'antd';
import { twMerge } from '@shared/utils/twMerge';
import { ControlProps } from '@jsonforms/core';
import { Input } from '../Input/Input';
export type CustomInputNumberProps = InputNumberProps & {
  label?: string;
  errors?: string;
  inOneLine?: boolean;
  classNameInputWrapper?: string;
  classNameLabel?: string;
  extraHelper?: ReactNode;
  minLength?: number;
  schema?: ControlProps['schema'];
};

const inputWrapperDefault = 'max-w-[41.2rem] grow';

const numbersOnly = new RegExp(/^-?\d*\.{0,1}\d*$/);

export const InputNumber = forwardRef<HTMLInputElement, CustomInputNumberProps>(
  ({ inOneLine, required, extraHelper, ...props }: CustomInputNumberProps, ref) => {
    const styleInOneLineWrapper = inOneLine ? 'flex-row justify-between' : '';
    const styleInOneLineLabel = inOneLine ? 'pt-[1.2rem]' : '';
    const { onInput, label, ...rest } = props;
    const [value, setValue] = useState(props.value ?? '');
    const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = target;

      let formattedValue: number | string = Number(value);
      if ((value as string).endsWith('.') || value === '' || isNaN(+value)) {
        formattedValue = value;
      }

      if (!numbersOnly.test(value)) {
        return;
      }
      setValue(formattedValue);
      props?.onChange?.(formattedValue);
    };
    const handleBlur = () => {
      if (value === '-' || value === '.') {
        setValue('');
        props?.onChange?.('');
      }
    };

    return (
      <div
        className="w-full"
        data-testid={`${props.name}-input-container`}
      >
        <label
          className={twMerge('flex w-full flex-col', styleInOneLineWrapper, props.classNameLabel)}
        >
          <div className={twMerge('flex items-center', styleInOneLineLabel)}>
            {label && (
              <span className={twMerge('text-1424 font-semibold', required && 'field-required')}>
                {label}
              </span>
            )}
            {extraHelper}
          </div>
          <div className={twMerge(inputWrapperDefault, props.classNameInputWrapper)}>
            <Input
              {...rest}
              value={value}
              onChange={handleChange}
              min={Number(props?.schema?.minimum)}
              max={Number(props?.schema?.maximum)}
              step={1}
              placeholder={props?.schema?.description ?? props.placeholder ?? label ?? ''}
              ref={ref}
              onBlur={handleBlur}
            />
            {!props.schema && props.errors && (
              <span
                data-testid={`${props.name}-error-container`}
                className={twMerge('ant-input-error text-1420 font-normal', props.className)}
              >
                {props.errors}
              </span>
            )}
          </div>
        </label>
      </div>
    );
  }
);
